import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useEditEvent,
  useRetrieveCurrentPlanzone,
  useRetrieveEvent,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Assignable } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { Loader } from "@common/loader";
import { TagList } from "@common/tagList";
import { DatesPickers } from "@scenes/event/datesPickers";
import { Comments } from "@scenes/comments";
import { Safeguard } from "@scenes/safeguard";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { ElementAttachments } from "@common/elementAttachments";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { ParticipantsList } from "@scenes/event/participantsList";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Props } from "./types";
import { Details } from "./details";
import { Timestamp } from "./timestamp";
import styles from "./styles.scss";

function EventPage({ eventId }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestFailure,
    event,
    hasRetrievedEvent,
    retrieveEvent,
  } = useRetrieveEvent(eventId);
  const {
    error,
    isRequestPending: isEditRequestPending,
    editEvent,
  } = useEditEvent(event);
  const { pushView } = useModal();
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    event.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];

  React.useEffect(() => {
    !hasRetrievedEvent && retrieveEvent(eventId);
  }, [eventId, hasRetrievedEvent]);

  return (
    <WorkspaceContext type="project" project={event.project}>
      <Safeguard
        useCases={[UseCases.editEvent, UseCases.postComment]}
        context={{ event }}
      >
        {({ canEditEvent, canPostComment }) => (
          <div
            className={classNames(
              { [styles.event]: !isRequestPending },
              { [styles.eventLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : isRequestFailure ? (
              <div className={styles.scenePlaceholder}>
                <ScenePlaceholder goBack={goBack} />
              </div>
            ) : (
              <div className={styles.contentModal}>
                <div className={styles.header}>
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        breadcrumbs={[
                          {
                            name: event.project.name,
                            to: `/projects/${event.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          {
                            name: event.list.name,
                            to: `/lists/${event.list.uuid}`,
                          },
                          { name: event.name },
                        ]}
                      />
                    </h1>

                    <div className={styles.rightContent}>
                      <div className={styles.contextualMenu}>
                        <Timestamp event={event} />
                      </div>
                      <div className={styles.profileNavigation}>
                        <AssistantShortcut />
                        <Notifications />
                        <ProfileNavigation />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.main}>
                  <div className={styles.content}>
                    <div className={styles.scrollWrapper}>
                      <div className={styles.details}>
                        <Details
                          event={event}
                          error={error}
                          readOnly={!canEditEvent}
                        />
                      </div>
                      <div className={styles.list}>
                        <ParticipantsList
                          project={event.project}
                          participants={event.participants}
                          select={
                            canEditEvent
                              ? (resources) =>
                                  editEvent({
                                    event,
                                    nextEvent: {
                                      ...event,
                                      participants: [
                                        ...event.participants,
                                        ...resources,
                                      ],
                                    },
                                  })
                              : undefined
                          }
                          remove={
                            canEditEvent
                              ? (resources) =>
                                  editEvent({
                                    event,
                                    nextEvent: {
                                      ...event,
                                      participants: event.participants.filter(
                                        ({ uuid }) =>
                                          !resources.find(
                                            (resource) =>
                                              uuid === resource.uuid,
                                          ),
                                      ),
                                    },
                                  })
                              : undefined
                          }
                        />
                      </div>
                      <div className={styles.tagList}>
                        <TagList
                          id={event.uuid}
                          tags={event.tags}
                          type={Assignable.EVENT}
                          project={event.project}
                          readOnly={!canEditEvent}
                        />
                      </div>
                      <Safeguard
                        useCases={[
                          UseCases.editProjectElementDetailsConfiguration,
                        ]}
                        context={{ event }}
                      >
                        {({ canEditProjectElementDetailsConfiguration }) =>
                          (template.length > 0 ||
                            canEditProjectElementDetailsConfiguration) && (
                            <div className={styles.secondaryCriteria}>
                              <div className={styles.row}>
                                <p className={styles.label}>
                                  {t("retrieveEvent.secondaryCriteria")}
                                </p>

                                {canEditProjectElementDetailsConfiguration ? (
                                  <div
                                    className={styles.configurationIconBox}
                                    onClick={() =>
                                      pushView({
                                        key: UseCases.retrieveProjectElementDetailsConfiguration,
                                        data: { project: event.project },
                                      })
                                    }
                                  >
                                    <CogWheelIcon
                                      className={styles.configurationIcon}
                                    />
                                  </div>
                                ) : (
                                  <div className={styles.contextualMenuIcon} />
                                )}
                              </div>
                              <ProjectElementsSecondaryCriteria
                                project={event.project}
                                criteriaForms={template.reduce(
                                  (acc, criteria) => ({
                                    ...acc,
                                    [criteria.uuid]: (
                                      <SecondaryCriteriaForm
                                        criteria={criteria}
                                        value={
                                          event.attributes
                                            ? event.attributes[criteria.uuid] ||
                                              ""
                                            : ""
                                        }
                                        flexible
                                        disabled={isEditRequestPending}
                                        submit={(value) =>
                                          editEvent({
                                            event,
                                            nextEvent: {
                                              ...event,
                                              attributes: {
                                                ...event.attributes,
                                                [criteria.uuid]: value,
                                              },
                                            },
                                          })
                                        }
                                      />
                                    ),
                                  }),
                                  {},
                                )}
                              />
                            </div>
                          )
                        }
                      </Safeguard>
                      <ElementAttachments
                        element={event}
                        project={event.project}
                        elementType="EVENT"
                        readOnly={!canEditEvent}
                      />
                    </div>
                  </div>
                  <div className={styles.timeline}>
                    <div className={styles.timestamp}>
                      <DatesPickers
                        event={event}
                        error={error}
                        readOnly={!canEditEvent}
                      />
                    </div>
                    <div className={styles.comments}>
                      {!event.project.isTemplate && (
                        <Comments
                          subject={event.uuid}
                          type="event"
                          canPost={canPostComment}
                          project={event.project.uuid}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { EventPage };
